import { t as tr } from './i18n';
import { comparablePrice } from './analyse';
import { fmtNum, lei } from './format';
import type { PurchaseOrder } from './po';
import { productKey, type PriceQuote } from './prices';
import type { Offer, RfqItem, SupplierRef } from './types';

/**
 * Reperul de preț: ce spune istoricul firmei despre un preț primit acum.
 *
 * „+7% față de media ta din ultimele 6 luni”, „cel mai mic preț primit
 * vreodată”, „cu 3% peste ce ai plătit ultima dată”. Istoricul de prețuri
 * există deja (`prices.ts`); aici se transformă în ceva care se poate pune
 * lângă un preț, în clipa deciziei.
 *
 * Trei reguli, ca reperul să nu mintă:
 * - **O valoare pe cerere.** Fiecare cerere trecută contribuie cu un singur
 *   preț: cel plătit, dacă s-a emis comanda, altfel cea mai mică ofertă. O
 *   ofertă umflată pe care ai refuzat-o nu trage media în sus, iar o cerere cu
 *   cinci oferte nu cântărește de cinci ori.
 * - **Același produs, aceeași unitate** (`productKey`).
 * - **Destule date.** Sub `BENCH_MIN` cereri anterioare nu spunem nimic: o
 *   „medie” dintr-un singur preț e doar prețul de data trecută.
 */

/** Cât de departe în urmă se uită media. */
export const BENCH_MONTHS = 6;
/** De la câte cereri anterioare are sens o medie sau un „record”. */
export const BENCH_MIN = 2;

/** Un preț plătit: o poziție dintr-o comandă emisă, în lei fără TVA. */
export interface PaidPrice {
  price: number;
  supplier: SupplierRef;
  /** ISO 8601, ziua emiterii comenzii */
  at: string;
  rfqId: string;
  desc: string;
  unit: string;
}

/** Prețurile plătite, din comenzile în vigoare (cele anulate nu s-au plătit). */
export function paidPrices(
  orders: Pick<PurchaseOrder, 'rfqId' | 'status' | 'supplier' | 'issuedAt' | 'data'>[],
): PaidPrice[] {
  const out: PaidPrice[] = [];
  for (const o of orders) {
    if (o.status !== 'issued') continue;
    const fx = o.data.currency === 'EUR' ? (o.data.fxRate ?? 1) : 1;
    for (const l of o.data.lines) {
      if (!(l.unitPrice > 0)) continue;
      out.push({
        price: l.unitPrice * fx,
        supplier: o.supplier,
        at: o.issuedAt,
        rfqId: o.rfqId,
        desc: l.desc,
        unit: l.unit,
      });
    }
  }
  return out;
}

/** Prețul de referință al unei cereri trecute, pentru un produs. */
export interface BenchPoint {
  rfqId: string;
  price: number;
  supplier: SupplierRef;
  at: string;
  /** plătit (din comandă) sau doar ofertat (cea mai mică ofertă) */
  paid: boolean;
}

export interface Benchmark {
  key: string;
  /** media pe ultimele `BENCH_MONTHS` luni; null sub `BENCH_MIN` cereri */
  avg: number | null;
  /** câte cereri sunt în medie */
  basis: number;
  /** câte cereri anterioare au produsul, din tot istoricul */
  requests: number;
  /** cel mai mic preț ofertat vreodată (nu doar câștigător) */
  low: PriceQuote | null;
  /** ultimul preț plătit; null dacă nu s-a comandat încă prin Procumi */
  lastPaid: PaidPrice | null;
  /** valorile din spatele mediei, cea mai recentă prima */
  points: BenchPoint[];
}

/** Reperele pozițiilor unei cereri, după id-ul poziției (lipsesc cele fără istoric). */
export type RfqBench = Record<string, Benchmark>;

export interface BenchOptions {
  /** ISO 8601: ce s-a întâmplat după nu contează (de obicei acum) */
  asOf: string;
  /** cererea la care te uiți: prețurile ei nu sunt „istoric” */
  exclude?: string;
}

const before = (iso: string, months: number): string => {
  const d = new Date(iso);
  d.setUTCMonth(d.getUTCMonth() - months);
  return d.toISOString();
};

const cheaperOrOlder = <T extends { price: number; at: string }>(a: T, b: T): T =>
  b.price < a.price || (b.price === a.price && b.at < a.at) ? b : a;

/**
 * Reperele, pe produs (cheia e `productKey`). Produsele fără nicio cerere
 * anterioară lipsesc din rezultat.
 */
export function benchmarks(
  quotes: PriceQuote[],
  paid: PaidPrice[],
  { asOf, exclude }: BenchOptions,
): Map<string, Benchmark> {
  const keep = (q: { rfqId: string; at: string }) => q.rfqId !== exclude && q.at <= asOf;

  // produs → cerere → cea mai mică ofertă / cel mai mic preț plătit
  const offered = new Map<string, Map<string, PriceQuote>>();
  for (const q of quotes) {
    if (!keep(q)) continue;
    const key = productKey(q.desc, q.unit);
    const byRfq = offered.get(key) ?? new Map<string, PriceQuote>();
    const prev = byRfq.get(q.rfqId);
    byRfq.set(q.rfqId, prev ? cheaperOrOlder(prev, q) : q);
    offered.set(key, byRfq);
  }
  const bought = new Map<string, Map<string, PaidPrice>>();
  for (const p of paid) {
    if (!keep(p)) continue;
    const key = productKey(p.desc, p.unit);
    const byRfq = bought.get(key) ?? new Map<string, PaidPrice>();
    const prev = byRfq.get(p.rfqId);
    byRfq.set(p.rfqId, prev ? cheaperOrOlder(prev, p) : p);
    bought.set(key, byRfq);
  }

  const from = before(asOf, BENCH_MONTHS);
  const out = new Map<string, Benchmark>();
  for (const key of new Set([...offered.keys(), ...bought.keys()])) {
    const off = offered.get(key) ?? new Map<string, PriceQuote>();
    const pay = bought.get(key) ?? new Map<string, PaidPrice>();

    const points: BenchPoint[] = [];
    for (const rfqId of new Set([...off.keys(), ...pay.keys()])) {
      const p = pay.get(rfqId);
      const q = off.get(rfqId);
      const ref = p ?? q!;
      points.push({ rfqId, price: ref.price, supplier: ref.supplier, at: ref.at, paid: !!p });
    }
    points.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));

    const recent = points.filter((p) => p.at >= from);
    const avg = recent.length >= BENCH_MIN ? recent.reduce((n, p) => n + p.price, 0) / recent.length : null;

    const lowQuotes = [...off.values()];
    const low = lowQuotes.length ? lowQuotes.reduce(cheaperOrOlder) : null;
    const lastPaid = [...pay.values()].reduce<PaidPrice | null>(
      (last, p) => (!last || p.at > last.at ? p : last),
      null,
    );

    out.set(key, {
      key,
      avg,
      basis: avg == null ? 0 : recent.length,
      requests: points.length,
      low,
      lastPaid,
      points,
    });
  }
  return out;
}

/** Ce spune istoricul despre un preț anume. Variațiile sunt fracții: +0,071 = +7,1%. */
export interface PriceVerdict {
  /** față de media pe 6 luni; null fără medie */
  vsAvg: number | null;
  /** sub cel mai mic preț primit vreodată (cu destul istoric cât să conteze) */
  record: boolean;
  /** față de ultimul preț plătit; null dacă n-ai cumpărat încă prin Procumi */
  vsPaid: number | null;
}

export function judgePrice(price: number, b: Benchmark | undefined): PriceVerdict {
  if (!b || !(price > 0)) return { vsAvg: null, record: false, vsPaid: null };
  return {
    vsAvg: b.avg == null ? null : (price - b.avg) / b.avg,
    record: b.requests >= BENCH_MIN && !!b.low && price < b.low.price,
    vsPaid: b.lastPaid ? (price - b.lastPaid.price) / b.lastPaid.price : null,
  };
}

/** „7,1%”, „12%”: o zecimală sub 10%, fără peste. */
function pct(f: number): string {
  const p = Math.abs(f) * 100;
  return `${fmtNum(p >= 10 ? Math.round(p) : Math.round(p * 10) / 10)}%`;
}

/** Sub atât, diferența față de medie e zgomot: „la nivelul mediei”. */
const FLAT = 0.005;
/** De la atât peste medie, nota se vede ca avertizare. */
const HIGH = 0.1;
/** De la atât sub medie, e o economie care merită arătată. */
const LOW = 0.02;

export interface VerdictNote {
  text: string;
  /** 'good': sub medie sau record; 'high': mult peste medie */
  tone: 'good' | 'high' | 'plain';
}

/** Nota de sub un preț din comparație; null când istoricul nu are ce spune. */
export function verdictNote(v: PriceVerdict): VerdictNote | null {
  if (v.record) return { text: tr('cel mai mic primit vreodată'), tone: 'good' };
  if (v.vsAvg == null) return null;
  if (Math.abs(v.vsAvg) < FLAT) return { text: tr('la nivelul mediei'), tone: 'plain' };
  const text = tr('{0}{1} față de medie', v.vsAvg > 0 ? '+' : '−', pct(v.vsAvg));
  return { text, tone: v.vsAvg >= HIGH ? 'high' : v.vsAvg <= -LOW ? 'good' : 'plain' };
}

/**
 * Rândul de sub poziție: reperul însuși. „media ta: 435 lei/mc · plătit ultima
 * dată: 450 lei/mc”. Prețul plătit se arată și dintr-o singură cerere: e un
 * fapt, nu o medie.
 */
export function benchLine(b: Benchmark | undefined, unit: string): string {
  if (!b) return '';
  const u = unit ? `lei/${unit}` : tr('lei');
  const parts: string[] = [];
  if (b.avg != null) parts.push(tr('media ta: {0} {1}', fmtNum(Math.round(b.avg * 100) / 100), u));
  if (b.lastPaid) {
    parts.push(tr('plătit ultima dată: {0} {1}', fmtNum(Math.round(b.lastPaid.price * 100) / 100), u));
  }
  return parts.join(' · ');
}

/** Explicația reperului, pentru `title`: din ce e făcută media. */
export function benchTitle(b: Benchmark | undefined): string {
  if (!b) return '';
  const lines: string[] = [];
  if (b.avg != null) {
    lines.push(
      tr(
        'Media pe ultimele {0} luni, din {1} cereri: din fiecare, prețul plătit sau cea mai mică ofertă.',
        BENCH_MONTHS,
        b.basis,
      ),
    );
  }
  if (b.low && b.requests >= BENCH_MIN) {
    lines.push(
      tr(
        'Cel mai mic primit: {0} lei, de la {1}.',
        fmtNum(Math.round(b.low.price * 100) / 100),
        b.low.supplier.name,
      ),
    );
  }
  if (b.lastPaid) lines.push(tr('Plătit ultima dată la {0}.', b.lastPaid.supplier.name));
  return lines.join('\n');
}

/**
 * O ofertă întreagă față de istoric, în lei: „cu 1.240 lei peste ce ai plătit
 * ultima dată”. Pe fiecare poziție, referința e ultimul preț plătit (un fapt)
 * sau, dacă n-ai comandat încă prin Procumi, media; pozițiile fără niciuna nu
 * intră. Se socotește la prețul listat × cantitatea cerută: comanda minimă e
 * altă discuție, iar aici întrebarea e doar dacă prețul e bun.
 */
export interface HistoryDiff {
  /** lei: cât costă acum pozițiile comparate, minus cât ar fi costat la referință */
  diff: number;
  /** fracție față de referință: +0,031 = +3,1% */
  rel: number;
  /** câte poziții au intrat în calcul */
  covered: number;
  /** câte poziții are oferta cu preț comparabil */
  priced: number;
  /** din ce e referința: prețuri plătite, medii sau amândouă */
  basis: 'paid' | 'avg' | 'mixed';
}

export function historyDiff(items: RfqItem[], offer: Offer, bench: RfqBench): HistoryDiff | null {
  let now = 0;
  let ref = 0;
  let covered = 0;
  let priced = 0;
  let paid = 0;
  for (const it of items) {
    const p = comparablePrice(offer, it);
    if (p == null) continue;
    priced++;
    const b = bench[it.id];
    const r = b?.lastPaid?.price ?? b?.avg;
    if (r == null || !(r > 0)) continue;
    covered++;
    if (b!.lastPaid) paid++;
    now += p * it.qty;
    ref += r * it.qty;
  }
  if (!covered || !(ref > 0)) return null;
  const basis = paid === covered ? 'paid' : paid === 0 ? 'avg' : 'mixed';
  return { diff: now - ref, rel: (now - ref) / ref, covered, priced, basis };
}

const BASIS_TEXT: Record<HistoryDiff['basis'], string> = {
  get paid() {
    return tr('ce ai plătit ultima dată');
  },
  get avg() {
    return tr('media ta');
  },
  get mixed() {
    return tr('istoricul tău');
  },
};

/** Pentru rândul din comparație: „+1.240 lei · +3,1%”, cu tonul notelor de preț. */
export function historyDiffShort(d: HistoryDiff): VerdictNote {
  if (Math.abs(d.rel) < FLAT) return { text: tr('la nivelul istoricului'), tone: 'plain' };
  const sign = d.diff > 0 ? '+' : '−';
  return {
    text: `${sign}${lei(Math.abs(d.diff))} · ${sign}${pct(d.rel)}`,
    tone: d.rel >= HIGH ? 'high' : d.rel <= -LOW ? 'good' : 'plain',
  };
}

/** Propoziția întreagă, pentru ecranul unde alegi furnizorul. */
export function historyDiffText(d: HistoryDiff): string {
  const part = d.covered < d.priced ? tr(', pe {0} din {1} poziții', d.covered, d.priced) : '';
  const vs = BASIS_TEXT[d.basis];
  if (Math.abs(d.rel) < FLAT) return tr('La nivelul istoricului tău{0}.', part);
  return d.diff > 0
    ? tr('Cu {0} ({1}) peste {2}{3}.', lei(Math.abs(d.diff)), pct(d.rel), vs, part)
    : tr('Cu {0} ({1}) sub {2}{3}.', lei(Math.abs(d.diff)), pct(d.rel), vs, part);
}

/** Explicația rândului, pentru `title`. */
export function historyDiffTitle(d: HistoryDiff): string {
  return [
    historyDiffText(d),
    tr(
      'Pe fiecare poziție: ultimul preț plătit sau, dacă n-ai comandat-o încă prin Procumi, media pe {0} luni. Prețul listat × cantitatea cerută.',
      BENCH_MONTHS,
    ),
  ].join('\n');
}
